import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Search, ChevronDown, Package, Accessibility, Wind, ArrowUpFromLine,
  BedDouble, Thermometer, Paintbrush, Droplets, type LucideIcon,
} from 'lucide-react';

// ─── BomSelector — Type/Brand/Model checkbox-tree picker for equipment-scope tags ──────────────
// Extracted from Inventory.tsx (was a page-local component only used by the catalogue item
// form's "Used For" field) into its own file so LearningHub.tsx's EntryFormModal can reuse the
// exact same picker for learning_hub_entries.used_for, without Inventory.tsx <-> LearningHub.tsx
// importing each other in both directions. Behavior is unchanged from the original — same tag
// format ('type:all' / 'type:brand:all' / 'type:brand:model'), same two-column drill-down UI.

type BomEntry = { id: number; type: string; displayName: string; brand: string | null; model: string | null; facilitySupplyType: string | null };
type BomGrouped = Record<string, BomEntry[]>;

const TYPE_ICONS: Record<string, LucideIcon> = {
  wheelchair: Accessibility, concentrator: Wind, lift: ArrowUpFromLine,
  bed: BedDouble, hvac: Thermometer, paint: Paintbrush,
  other: Package, shower_chair: Droplets, shower_chairs: Droplets,
};
function getEquipmentIcon(type: string) {
  const Comp = TYPE_ICONS[type.toLowerCase()] ?? Package;
  return <Comp size={16} color="var(--text-secondary)" />;
}

export function BomSelector({ selected, onChange, equipmentData }: { selected: string[]; onChange: (v: string[]) => void; equipmentData: { types: any[] } }) {
  const [open, setOpen] = useState(false);
  const [bomData, setBomData] = useState<BomGrouped>({});
  const [drillType, setDrillType] = useState<string | null>(null);
  const [drillBrand, setDrillBrand] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ left: 0, width: 0 });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const grouped: BomGrouped = {};
    for (const t of (equipmentData.types || [])) {
      grouped[t.type] = [
        { id: 0, type: t.type, displayName: t.displayName, brand: null, model: null, facilitySupplyType: null },
        ...(t.brands || []).flatMap((b: { brand: string; models: string[] }) => [
          { id: 0, type: t.type, displayName: t.displayName, brand: b.brand, model: null, facilitySupplyType: null },
          ...b.models.map((m: string) => ({ id: 0, type: t.type, displayName: t.displayName, brand: b.brand, model: m, facilitySupplyType: null })),
        ]),
      ];
    }
    setBomData(grouped);
  }, [equipmentData]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) { setOpen(false); setDrillType(null); setDrillBrand(null); setSearchQuery(''); }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const maxDropdownH = 250;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < maxDropdownH) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left, width: rect.width });
      } else {
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
      }
    }
    setOpen((prev) => !prev);
  }

  function toggleValue(val: string) {
    onChange(selected.includes(val) ? selected.filter((s) => s !== val) : [...selected, val]);
  }

  function formatTag(val: string) {
    const parts = val.split(':');
    const type = parts[0];
    const brand = parts[1];
    const model = parts[2];
    const typeEntry = (bomData[type] || []).find((e) => e.brand === null && e.model === null);
    const typeName = typeEntry?.displayName || type;
    if (!brand || brand === 'all') return `All ${typeName}`;
    if (!model || model === 'all') return `${brand} ${typeName}`;
    return `${brand} ${model}`;
  }

  const types = Object.keys(bomData);
  const filteredTypes = searchQuery
    ? types.filter((type) => {
        const entry = (bomData[type] || []).find((e) => e.brand === null && e.model === null);
        const typeName = (entry?.displayName || type).toLowerCase();
        const brands = (bomData[type] || []).filter((e) => e.brand !== null && e.model === null).map((e) => e.brand as string);
        const q = searchQuery.toLowerCase();
        return typeName.includes(q) || brands.some((b) => b.toLowerCase().includes(q));
      })
    : types;

  const checkboxStyle = (checked: boolean): React.CSSProperties => ({
    width: 13, height: 13, borderRadius: 3, border: '2px solid #6366f1',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: checked ? '#6366f1' : 'transparent', flexShrink: 0,
  });

  return (
    <div ref={containerRef}>
      <div
        onClick={openDropdown}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center',
          padding: '6px 10px', border: '1px solid rgba(255,255,255,0.13)',
          borderRadius: 6, background: 'var(--bg-elevated)', cursor: 'pointer',
          minHeight: 34, boxSizing: 'border-box',
        }}
      >
        {selected.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>Select equipment…</span>}
        {selected.map((val) => (
          <span key={val} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px 2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(99,102,241,0.2)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.35)' }}>
            {formatTag(val)}
            <button type="button" onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); toggleValue(val); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#818cf8', lineHeight: 1, fontSize: '1rem' }}>×</button>
          </span>
        ))}
        <ChevronDown size={14} style={{ opacity: 0.5, marginLeft: 'auto', flexShrink: 0 }} />
      </div>
      {open && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed',
          ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
          left: dropdownPos.left, minWidth: Math.max(dropdownPos.width || 0, 420),
          zIndex: 10001, background: 'var(--bg-elevated)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
          color: 'var(--text-primary)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', maxHeight: 320,
        }}>
          {/* Search bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              autoFocus
              placeholder="Search all equipment…"
              value={searchQuery}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ fontSize: 12, background: 'transparent', border: 'none', outline: 'none', flex: 1, color: 'var(--text-primary)' }}
            />
          </div>

          {/* Two-column split */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, overflow: 'hidden', minHeight: 0 }}>
            {/* LEFT COLUMN — type / brand */}
            <div style={{ borderRight: '0.5px solid rgba(255,255,255,0.08)', overflowY: 'auto', maxHeight: 260 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', padding: '5px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'sticky' as const, top: 0, background: 'var(--bg-elevated)' }}>
                Type / Brand
              </div>
              {filteredTypes.map((type) => {
                const entry = (bomData[type] || []).find((e) => e.brand === null && e.model === null);
                const typeName = entry?.displayName || type;
                const brands = (bomData[type] || []).filter((e) => e.brand !== null && e.model === null).map((e) => e.brand as string);
                const isExpanded = drillType === type;
                return (
                  <div key={type}>
                    <div
                      onMouseDown={(e) => { e.stopPropagation(); setDrillType(isExpanded ? null : type); setDrillBrand(null); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px', cursor: 'pointer', background: isExpanded ? 'var(--bg-hover)' : 'transparent' }}
                      onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ color: 'var(--text-secondary)', flexShrink: 0, display: 'flex' }}>{getEquipmentIcon(type)}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{typeName}</span>
                      {brands.length > 0 && (
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 99, background: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)', flexShrink: 0 }}>{brands.length}</span>
                      )}
                    </div>
                    {isExpanded && (
                      <div>
                        <div
                          onMouseDown={(e) => { e.stopPropagation(); toggleValue(`${type}:all`); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px 7px 28px', cursor: 'pointer' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <span style={checkboxStyle(selected.includes(`${type}:all`))}>{selected.includes(`${type}:all`) && <span style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</span>}</span>
                          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>All {typeName}</span>
                        </div>
                        {brands.map((brand) => {
                          const brandVal = `${type}:${brand}:all`;
                          const brandChecked = selected.includes(brandVal);
                          const modelCount = (bomData[type] || []).filter((e) => e.brand === brand && e.model !== null).length;
                          const isActiveBrand = drillBrand === brand && drillType === type;
                          return (
                            <div
                              key={brand}
                              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 8px 7px 28px', cursor: 'pointer', background: isActiveBrand ? 'rgba(59,130,246,0.08)' : 'transparent' }}
                              onMouseEnter={(e) => { if (!isActiveBrand) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                              onMouseLeave={(e) => { if (!isActiveBrand) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <span onMouseDown={(e) => { e.stopPropagation(); toggleValue(brandVal); }} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                <span style={checkboxStyle(brandChecked)}>{brandChecked && <span style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</span>}</span>
                              </span>
                              <span
                                onMouseDown={(e) => { e.stopPropagation(); if (modelCount > 0) setDrillBrand(isActiveBrand ? null : brand); }}
                                style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}
                              >{brand}</span>
                              {modelCount > 0 && (
                                <>
                                  <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 99, background: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)', flexShrink: 0 }}>{modelCount}</span>
                                  <span onMouseDown={(e) => { e.stopPropagation(); setDrillBrand(isActiveBrand ? null : brand); }} style={{ fontSize: 13, color: 'var(--text-muted)', flexShrink: 0, cursor: 'pointer' }}>›</span>
                                </>
                              )}
                            </div>
                          );
                        })}
                        {brands.length === 0 && (
                          <div style={{ padding: '5px 10px 5px 24px', fontSize: 11, color: 'var(--text-muted)' }}>No brands</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredTypes.length === 0 && (
                <div style={{ padding: '12px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' as const }}>No equipment found</div>
              )}
            </div>

            {/* RIGHT COLUMN — models */}
            <div style={{ overflowY: 'auto', maxHeight: 260 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', padding: '5px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'sticky' as const, top: 0, background: 'var(--bg-elevated)' }}>
                Model
              </div>
              {!drillBrand && (
                <div style={{ padding: '20px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' as const }}>
                  Select a brand to see models
                </div>
              )}
              {drillBrand && drillType && (() => {
                const models = (bomData[drillType] || []).filter((e) => e.brand === drillBrand && e.model !== null).map((e) => e.model as string);
                const allBrandVal = `${drillType}:${drillBrand}:all`;
                const allBrandChecked = selected.includes(allBrandVal);
                if (models.length === 0) return (
                  <div style={{ padding: '10px', fontSize: 11, color: 'var(--text-muted)' }}>No models — select brand to use all</div>
                );
                return (
                  <>
                    <div
                      onMouseDown={(e) => { e.stopPropagation(); toggleValue(allBrandVal); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={checkboxStyle(allBrandChecked)}>{allBrandChecked && <span style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</span>}</span>
                      <span style={{ fontSize: 13, color: allBrandChecked ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>All {drillBrand}</span>
                    </div>
                    {models.map((model) => {
                      const val = `${drillType}:${drillBrand}:${model}`;
                      const checked = selected.includes(val);
                      return (
                        <div
                          key={model}
                          onMouseDown={(e) => { e.stopPropagation(); toggleValue(val); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <span style={checkboxStyle(checked)}>{checked && <span style={{ color: '#fff', fontSize: 8, lineHeight: 1 }}>✓</span>}</span>
                          <span style={{ fontSize: 13, color: checked ? 'var(--accent-primary)' : 'var(--text-primary)' }}>{model}</span>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderTop: '0.5px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{selected.length} selected</span>
            {selected.length > 0 && (
              <span
                onMouseDown={(e) => { e.stopPropagation(); onChange([]); }}
                style={{ fontSize: 11, color: 'var(--accent-primary)', cursor: 'pointer' }}
              >Clear all</span>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
